/* eslint-disable no-param-reassign */
import { addPupilNames } from 'helpers/homework';
import updateProgressFromCompletedQuiz from '../helpers/quiz-progress';
import updateHomeworkFromProgress from '../helpers/homework-progress';

const getInitialState = () => ({
  homeworks: undefined,
  hasWholeSchoolHomeworks: false,
});

function getDefaultHomeworkGoalProgressState(goalId) {
  return {
    goalId,
    myGoal: {},
    readiness: {},
    learn: {
      videos: {},
    },
    do: {
      videos: {},
    },
    quiz: {},
    remember: {},
  };
}

const homeworkSlice = (set, get) => ({
  sliceName: 'homework',
  getInitialState,
  onConnected: (data) => {
    const { myHomework } = data;
    const { setHomeworks } = get();
    if (myHomework) {
      setHomeworks(myHomework);
    }
  },
  selectors: {
    ...getInitialState(),

    getHomework: (homeworkId) =>
      get().homeworks.find((homework) => homework.id === homeworkId),
  },
  mutators: {
    updateHomework: (homework) =>
      set((draft) => {
        const { pupils } = get();
        // We need to do this here since the socket action handler is called before the API respTransform
        const homeWorkWithPupilNames = addPupilNames(homework, pupils);

        const homeworkIndex = draft.homeworks.findIndex(
          (h) => h.id === homework.id
        );

        if (homeworkIndex !== -1) {
          draft.homeworks[homeworkIndex] = {
            ...draft.homeworks[homeworkIndex],
            ...homeWorkWithPupilNames,
          };
        } else {
          draft.homeworks.push(homeWorkWithPupilNames);
        }
      }),

    setHomeworks: (homeworks, wholeSchool) =>
      set((draft) => {
        draft.homeworks = homeworks;
        draft.hasWholeSchoolHomeworks = wholeSchool;
      }),

    setHomeworkProgress: (homeworkId, data) =>
      set((draft) => {
        const { goalsProgress, memoryBoost, focusFive } = data;
        draft.homeworks = draft.homeworks.map((homework) => {
          if (homework.id === homeworkId) {
            updateHomeworkFromProgress(
              homework,
              goalsProgress,
              draft.myCourses,
              focusFive,
              memoryBoost
            );
            return {
              ...homework,
              goalsProgress,
            };
          }
          return homework;
        });
      }),

    setHomeworkVideoProgress: (
      goalId,
      step,
      videoId,
      videoProgress,
      homeworkId
    ) =>
      set((draft) => {
        const homework = draft.homeworks.find((hw) => hw.id === homeworkId);
        if (homework) {
          const goalProgress =
            homework.goalsProgress[goalId] ??
            getDefaultHomeworkGoalProgressState(goalId);

          goalProgress[step] ??= {};
          goalProgress[step].videos ??= {};
          goalProgress[step].videos[videoId] = videoProgress;

          homework.goalsProgress[goalId] = goalProgress;
        }
      }),
    addHomeworkQuizAttempt: (goalId, step, attempt, homeworkId) =>
      set((draft) => {
        const homework = draft.homeworks.find((hw) => hw.id === homeworkId);
        if (homework) {
          const goalProgress =
            homework.goalsProgress[goalId] ??
            getDefaultHomeworkGoalProgressState(goalId);

          if (!attempt.completionDate) {
            goalProgress[step] ??= {};
            goalProgress[step].inProgressQuizAttemptId = attempt.id;
          }
          homework.goalsProgress[goalId] = goalProgress;
        }
      }),
    completeHomeworkQuizAttempt: (goalId, step, homeworkId) =>
      set((draft) => {
        const { currentQuizAttempt, homeworks } = draft;
        currentQuizAttempt.completionDate = new Date().toJSON();

        const homework = homeworks.find((hw) => hw.id === homeworkId);
        const goalsProgress = homework?.goalsProgress ?? {};
        const goalProgress = goalsProgress[goalId];
        const progress = goalProgress[step];

        updateProgressFromCompletedQuiz(
          progress,
          currentQuizAttempt,
          goalId,
          step
        );

        updateHomeworkFromProgress(homework, goalsProgress, draft.myCourses);
      }),

    removeHomework: (homework) =>
      set((draft) => {
        draft.homeworks = draft.homeworks.filter((h) => h.id !== homework.id);
      }),
  },
});

export default homeworkSlice;
